import * as React from 'react';

/**
 * Table primitives extracted from the two hand-rolled table dialects.
 *
 * - `dense` (default) — the report dialect, by far the most common:
 *   `min-w-full divide-y divide-gray-200`, `thead bg-gray-50`,
 *   `th px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider`,
 *   `td px-6 py-4 whitespace-nowrap`.
 * - `compact` — the card dialect: `w-full`, bare `thead`,
 *   `th text-left p-4 font-medium text-gray-500`, `td p-4`.
 *
 * Density is provided by <Table> via context, so <THead>/<TBody>/<Th>/<Td>
 * automatically match the table they sit in. Class sets are byte-compatible
 * with the hand-written copies these replace.
 *
 * `SortableTh` (reports) stays separate: it is the sortable variant of the
 * dense header cell and already matches these classes.
 */

import { DensityContext, type TableDensity } from './table-density';

export type { TableDensity };

function cx(...parts: Array<string | undefined | false>) {
  return parts.filter(Boolean).join(' ');
}

export function TableWrapper({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cx('overflow-x-auto', className)} {...props}>
      {children}
    </div>
  );
}

export interface TableProps extends React.TableHTMLAttributes<HTMLTableElement> {
  density?: TableDensity;
}

export function Table({ density = 'dense', className, children, ...props }: TableProps) {
  return (
    <DensityContext.Provider value={density}>
      <table
        className={cx(
          density === 'dense' ? 'min-w-full divide-y divide-gray-200' : 'w-full',
          className
        )}
        {...props}
      >
        {children}
      </table>
    </DensityContext.Provider>
  );
}

export function THead({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLTableSectionElement>) {
  const density = React.useContext(DensityContext);
  return (
    <thead className={cx(density === 'dense' && 'bg-gray-50', className) || undefined} {...props}>
      {children}
    </thead>
  );
}

export function TBody({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLTableSectionElement>) {
  const density = React.useContext(DensityContext);
  return (
    <tbody
      className={cx(density === 'dense' && 'bg-white divide-y divide-gray-200', className) || undefined}
      {...props}
    >
      {children}
    </tbody>
  );
}

export interface ThProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  align?: 'left' | 'right' | 'center';
}

export function Th({ align = 'left', className, children, ...props }: ThProps) {
  const density = React.useContext(DensityContext);
  const alignClass = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return (
    <th
      scope="col"
      className={cx(
        density === 'dense'
          ? `px-6 py-3 ${alignClass} text-xs font-medium text-gray-500 uppercase tracking-wider`
          : `${alignClass} p-4 font-medium text-gray-500`,
        className
      )}
      {...props}
    >
      {children}
    </th>
  );
}

export function Td({
  className,
  children,
  ...props
}: React.TdHTMLAttributes<HTMLTableCellElement>) {
  const density = React.useContext(DensityContext);
  return (
    <td
      className={cx(density === 'dense' ? 'px-6 py-4 whitespace-nowrap' : 'p-4', className)}
      {...props}
    >
      {children}
    </td>
  );
}
